import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppFloat from '../../components/feature/WhatsAppFloat';
import Button from '../../components/base/Button';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate form submission - replace with actual form handling
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: ''
      });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactMethods = [
    {
      icon: 'ri-phone-line',
      title: 'Phone Numbers',
      items: [
        { label: '🇳🇬 Nigeria', value: '09136628983', link: 'tel:+2349136628983' },
        { label: '🇬🇧 United Kingdom', value: '+44 7577336876', link: 'tel:+447577336876' },
        { label: '🇺🇸 United States', value: '+1 469-738-0696', link: 'tel:+14697380696' },
      ]
    },
    {
      icon: 'ri-mail-line',
      title: 'Email',
      items: [
        { label: 'General Inquiries', value: 'phoenixachieversteam@gmail.com', link: 'mailto:phoenixachieversteam@gmail.com' }
      ]
    },
    {
      icon: 'ri-whatsapp-line',
      title: 'WhatsApp',
      items: [
        { label: 'Quick Support', value: 'Chat with us instantly', link: 'https://wa.me/14697380696' }
      ]
    }
  ];

  const socialPlatforms = [
    { name: 'TikTok', icon: 'ri-tiktok-line', url: 'https://www.tiktok.com/@phoenixachieversteamllc', color: 'hover:text-black' },
    { name: 'Instagram', icon: 'ri-instagram-line', url: 'https://www.instagram.com/phoenixachieversteamllc/', color: 'hover:text-pink-500' },
    { name: 'Facebook', icon: 'ri-facebook-line', url: 'https://www.facebook.com/profile.php?id=61584440590340&sk=directory_links', color: 'hover:text-blue-600' },
    { name: 'Twitter/X', icon: 'ri-twitter-x-line', url: 'https://x.com/Phoenixteamllc', color: 'hover:text-black' },
    { name: 'Threads', icon: 'ri-at-line', url: 'https://www.instagram.com/phoenixachieversteamllc/', color: 'hover:text-black' },
    { name: 'Fiverr', icon: 'ri-briefcase-line', url: 'https://www.fiverr.com/users/abiodun136', color: 'hover:text-green-500' },
    { name: 'Upwork', icon: 'ri-building-line', url: 'https://www.upwork.com/agencies/1960314973182392391/', color: 'hover:text-green-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white font-['Poppins']">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-red-600 to-black text-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">Get In Touch</h1>
            <p className="text-xl lg:text-2xl opacity-90 leading-relaxed mb-8">
              Ready to take your business to the next level? Contact us today for a free consultation.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm lg:text-base">
              <span className="bg-white/20 px-4 py-2 rounded-full">Free Consultation</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Global Support</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Fast Response</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">Contact Information</h2>
              <p className="text-xl text-gray-600">Choose your preferred way to reach us</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
              {contactMethods.map((method, index) => (
                <div key={index} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className={`${method.icon} text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-black mb-6 text-center">{method.title}</h3>
                  <div className="space-y-4">
                    {method.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="text-center">
                        <p className="text-gray-600 text-sm mb-1">{item.label}</p>
                        <a 
                          href={item.link}
                          className="text-red-600 hover:text-red-700 font-semibold transition-colors"
                          {...(item.link.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                        >
                          {item.value}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Social Media Icons */}
            <div className="text-center mb-16">
              <h3 className="text-2xl font-bold text-black mb-8">Follow Us on Social Media</h3>
              <div className="flex flex-wrap justify-center gap-4">
                {socialPlatforms.map((platform, index) => (
                  <a 
                    key={index}
                    href={platform.url}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`flex flex-col items-center p-4 bg-gray-50 rounded-lg hover:bg-red-600 hover:text-white transition-all duration-300 group min-w-[120px]`}
                  >
                    <div className="w-12 h-12 flex items-center justify-center mb-2">
                      <i className={`${platform.icon} text-2xl text-red-600 group-hover:text-white transition-colors duration-300`}></i>
                    </div>
                    <div className="text-sm font-semibold text-center">{platform.name}</div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-black mb-6">Send Us a Message</h3>
                
                {submitStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
                    Thank you for your message! We'll get back to you within 24 hours.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
                    Sorry, there was an error sending your message. Please try again or contact us directly.
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form id="contact-inquiry">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                        placeholder="your.email@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                        placeholder="Your phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                        placeholder="Your company name"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                      Service Needed *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all"
                    >
                      <option value="">Select a service</option>
                      <option value="business-registration">Business Registration</option>
                      <option value="government-contracts">Government Contracting</option>
                      <option value="grant-writing">Grant Writing</option>
                      <option value="web-design">Web Design</option>
                      <option value="tax-services">Tax & Compliance</option>
                      <option value="consulting">General Consulting</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent transition-all resize-vertical"
                      placeholder="Tell us about your project or how we can help you..."
                    ></textarea>
                    <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white py-4 text-lg font-semibold whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>

              {/* Map & Additional Info */}
              <div className="space-y-8">
                {/* Business Hours */}
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-black mb-6">Business Hours</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monday - Friday</span>
                      <span className="font-semibold text-black">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Saturday</span>
                      <span className="font-semibold text-black">10:00 AM - 4:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sunday</span>
                      <span className="font-semibold text-black">Closed</span>
                    </div>
                    <div className="mt-4 p-3 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-700">
                        <i className="ri-time-line mr-2"></i>
                        All times are in your local timezone. For urgent matters, contact us via WhatsApp.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-black mb-6">Quick Actions</h3>
                  <div className="space-y-4">
                    <Button 
                      onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-3 whitespace-nowrap"
                    >
                      <i className="ri-calendar-line mr-2"></i>
                      Book Free Consultation
                    </Button>
                    <Button 
                      onClick={() => window.open('https://wa.me/14697380696', '_blank')}
                      className="w-full bg-green-500 hover:bg-green-600 text-white py-3 whitespace-nowrap"
                    >
                      <i className="ri-whatsapp-line mr-2"></i>
                      WhatsApp Chat
                    </Button>
                    <Button 
                      onClick={() => window.location.href = 'mailto:phoenixachieversteam@gmail.com'}
                      className="w-full bg-black hover:bg-gray-800 text-white py-3 whitespace-nowrap"
                    >
                      <i className="ri-mail-line mr-2"></i>
                      Send Email
                    </Button>
                  </div>
                </div>

                {/* Global Offices */}
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h3 className="text-2xl font-bold text-black mb-6">Global Presence</h3>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">🇺🇸</div>
                      <div>
                        <p className="font-semibold text-black">United States</p>
                        <p className="text-gray-600 text-sm">Serving all 50 states</p>
                        <p className="text-red-600 font-medium">+1 469-738-0696</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">🇬🇧</div>
                      <div>
                        <p className="font-semibold text-black">United Kingdom</p>
                        <p className="text-gray-600 text-sm">England, Scotland, Wales, N. Ireland</p>
                        <p className="text-red-600 font-medium">+44 7577336876</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className="text-2xl">🇳🇬</div>
                      <div>
                        <p className="font-semibold text-black">Nigeria</p>
                        <p className="text-gray-600 text-sm">All states and FCT</p>
                        <p className="text-red-600 font-medium">09136628983</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-black mb-6">Frequently Asked Questions</h2>
              <p className="text-xl text-gray-600">Quick answers to common questions</p>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-black mb-2">How quickly can you help with business registration?</h3>
                <p className="text-gray-600">Most business registrations are completed within 3-5 business days, depending on the jurisdiction and entity type.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-black mb-2">Do you offer services internationally?</h3>
                <p className="text-gray-600">Yes, we serve clients globally with primary focus on USA, UK, Nigeria, and Canada for business formation and compliance services.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-black mb-2">What's included in a free consultation?</h3>
                <p className="text-gray-600">Our free consultation includes project assessment, timeline estimation, cost breakdown, and personalized recommendations for your specific needs.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-lg font-bold text-black mb-2">Can you help with ongoing compliance after registration?</h3>
                <p className="text-gray-600">Absolutely! We provide ongoing support for tax filings, annual reports, amendments, and other compliance requirements.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Contact;